import type { TSubscriptionStatus } from '@escloud/types';

import type { BadgeProps } from '@/components/ui/badge';
import { ApiError } from '@/services/api-client';

/**
 * The one place the client interprets a subscription: the status it has, and
 * the failure that proves it has none (ESCLOUD-288).
 *
 * Screens used to read the status directly (the post-checkout landing page,
 * the billing screen's badge and the read-only banner), each with its own bare
 * literals. Nothing tied them to the shared vocabulary, so a status added
 * server-side simply fell through every one of them: the badge turned grey and
 * no test and no build failed.
 *
 * A `Record` over `TSubscriptionStatus` is what closes that hole. The shared
 * type is the single declaration of the vocabulary for the database, the wire,
 * the billing port and the Stripe adapter alike (ESCLOUD-291), so adding a
 * member to it stops the client compiling until this table answers for it —
 * every question, at once, in one row. That is deliberately the same forcing
 * function the server uses: `resolveAccessLevel`'s `never` witness refuses to
 * compile without a decision, and this refuses to compile without three.
 *
 * The questions live together because the *status* is what they share, not
 * because they are alike: waiting and presentation are answered on one row so
 * a new status cannot be half-classified. Access rights are NOT among
 * them — `accessLevel` arrives server-derived on the response and the client
 * must never re-derive it (see `useIsReadOnly`).
 *
 * The values here are `import type` only. `@escloud/types` emits CommonJS, and
 * a *value* import of the `SubscriptionStatus` const object passes vitest but
 * breaks `pnpm build:client` — Rollup cannot see named exports through the
 * `__exportStar` barrel. So the members appear here as object keys, which the
 * `Record` constrains just as tightly: a missing key and an unknown key are
 * both compile errors. The spec beside this file re-checks the same set against
 * the runtime enum, which it may safely import because a spec is not part of
 * the production bundle.
 */

/** Badge tone, pinned to what the `Badge` component actually accepts. */
export type TSubscriptionStatusBadge = NonNullable<BadgeProps['variant']>;

export interface ISubscriptionStatusFacts {
    /**
     * A valid outcome of the checkout that has just completed, i.e. the
     * post-checkout page may stop waiting and open the workspace.
     *
     * Narrower than "has full access" on purpose: `past_due` grants full access
     * server-side, but it is not something a just-completed checkout produces
     * (a first payment that fails leaves the subscription `incomplete`). Seeing
     * it on that page means an older lapsed row is still in the cache, and
     * treating it as success would report someone's purchase as done before it
     * is. So the two notions are kept apart, and this is the narrow one.
     */
    activatedAtCheckout: boolean;
    /** Tone of the status badge on the billing screen. */
    badge: TSubscriptionStatusBadge;
    /**
     * Parenthetical the read-only banner adds to name the reason, or `null` to
     * say nothing. Only worth adding where the status explains something the
     * banner does not already say.
     */
    readOnlyNote: string | null;
}

export const SUBSCRIPTION_STATUS_FACTS: Record<TSubscriptionStatus, ISubscriptionStatusFacts> = {
    incomplete: {
        activatedAtCheckout: false,
        badge: 'outline',
        readOnlyNote: null,
    },
    incomplete_expired: {
        activatedAtCheckout: false,
        badge: 'outline',
        readOnlyNote: null,
    },
    trialing: {
        activatedAtCheckout: true,
        badge: 'default',
        readOnlyNote: null,
    },
    active: {
        activatedAtCheckout: true,
        badge: 'default',
        readOnlyNote: null,
    },
    past_due: {
        activatedAtCheckout: false,
        badge: 'destructive',
        readOnlyNote: null,
    },
    unpaid: {
        activatedAtCheckout: false,
        badge: 'destructive',
        readOnlyNote: null,
    },
    canceled: {
        activatedAtCheckout: false,
        badge: 'secondary',
        readOnlyNote: 'subscription canceled',
    },
    paused: {
        activatedAtCheckout: false,
        badge: 'secondary',
        readOnlyNote: null,
    },
};

/**
 * What an already-deployed client assumes about a status it has never heard of.
 *
 * Every answer is the cautious one, and cautious here means *do not act*: no
 * early exit from the activation wait, a neutral badge, no invented
 * explanation. The tenant still gets exactly the access the server granted,
 * because `accessLevel` is read straight off the response and the server
 * refuses mutations regardless.
 *
 * This is the soft half of a deliberately two-sided policy: soft at runtime, so
 * a client older than the API degrades instead of breaking, and hard at build
 * time, where the `Record` above turns the same drift into a failed typecheck
 * before it can ever ship.
 */
const UNRECOGNISED_STATUS: ISubscriptionStatusFacts = {
    activatedAtCheckout: false,
    badge: 'outline',
    readOnlyNote: null,
};

/**
 * The table, read safely. Always use this rather than indexing the table: the
 * type says the key exists, and a server that has shipped a status this build
 * predates says otherwise.
 */
export function subscriptionStatusFacts(status: TSubscriptionStatus): ISubscriptionStatusFacts {
    return SUBSCRIPTION_STATUS_FACTS[status] ?? UNRECOGNISED_STATUS;
}

/**
 * Whether a failure of the subscription query PROVES the tenant has no
 * subscription, as opposed to leaving us not knowing (ESCLOUD-288).
 *
 * Only a 404 proves it. Every other failure — a 500, an expired token, an
 * offline browser — says nothing about whether a subscription exists, and
 * treating it as an absence is how a paying tenant gets offered "Choose a
 * plan" during an API blip and buys a second one.
 *
 * Here rather than beside the query in `use-subscription.ts`, where it also
 * belongs, for a practical reason: the router guard, the billing screen and
 * the post-checkout page all had their own copy of this two-term predicate,
 * and all three mock the hooks module wholesale in their specs — so a shared
 * predicate living there would have had to be re-declared in each mock, which
 * is the duplication over again, in the tests this time.
 */
export function isProvenNoSubscription(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404;
}
